// /w/:wid/quests — the quest book for the current city: rewards waiting (CLAIM), the chapters by category, the
// Aurora chapter with its objectives, and what unlocks more. Claiming pays into THIS city.
// Classic Frame (Oct 10 2026): the title bar and a strip with the counts and Claim all; every section is a full-width
// band that opens and closes (closed until opened, remembered on this device — state/collapse.ts, ids "quests.*"),
// and a closed band still shows how many of its quests are ready to claim.
import { useNavigate } from "react-router-dom";
import type { QuestItem } from "@/api/types";
import { api } from "@/api/client";
import { WorldPage, useWorldCity } from "@/app/WorldPage";
import { art } from "@/kit/art";
import { ScreenHero, UnitMedal } from "@/kit/city";
import { IconChevronRight, IconStar } from "@/kit/icons";
import { buildLink } from "@/screens/city/CityView";
import type { ReactNode } from "react";
import { Bar, Button, Card, CardHead, Empty, Medallion, Row, RowText, cx } from "@/kit/primitives";
import { useRememberedOpen } from "@/state/collapse";
import { BUILDING_KEYS, TROOP_KEYS, type BuildingKey, type TroopKey } from "@/game/constants";
import { fmtNum, plural } from "@/game/format";
import { auroraQuests, awardWords, questCategories, questList } from "@/game/city";
import "../social.css";

export function Quests() {
  return (
    <WorldPage layout="col">
      <QuestBook />
    </WorldPage>
  );
}

function QuestBook() {
  const { v, wid, vid, base, actions } = useWorldCity();
  const nav = useNavigate();
  if (!v) return null;
  const quests = questList(v);
  const byKey = new Map(quests.map((q) => [q.id, q]));
  const cats = questCategories(v);
  const grouped = cats.map((c) => ({ name: c.name, items: c.items.map((k) => byKey.get(k)).filter((q): q is QuestItem => !!q) })).filter((c) => c.items.length);
  const placed = new Set(grouped.flatMap((c) => c.items.map((q) => q.id)));
  const rest = quests.filter((q) => !placed.has(q.id));
  if (rest.length) grouped.push({ name: "More quests", items: rest });
  const claimable = quests.filter((q) => Number(q.done) === 1);
  const aurora = auroraQuests(v);
  const itemNames = Object.fromEntries(Object.entries(v.store?.items ?? {}).map(([k, it]) => [k, it.name]));
  const unlock = typeof v.unlock_text === "string" ? v.unlock_text : "";
  const claim = (q: QuestItem) => actions.run(`claim-${q.id}`, () => api.quest.collect({ wid, vid: vid! }, q.id), { success: `${q.name}: ${awardWords(q.award, itemNames)} collected.` });
  const claimAll = async () => { for (const q of claimable) await claim(q); };

  return (
    <div className="stack classic">
      <ScreenHero
        title="Quests"
        onBack={() => nav(base)}
        stats={[
          { label: "To claim", value: fmtNum(claimable.length), tone: claimable.length ? "green" : undefined },
          { label: "Open", value: fmtNum(quests.length - claimable.length) },
          { label: "Chapters", value: fmtNum(grouped.length) },
        ]}
      >
        <div className="tstrip-act">
          <p className="qs-lead">{claimable.length ? `${plural(claimable.length, "reward")} waiting — claimed rewards land in ${v.village.city_name}.` : "Finish a chapter and claim its reward here. Rewards land in the city you are looking at."}</p>
          {claimable.length > 1 && <Button kind="gold" size="md" busy={actions.anyBusy} onClick={() => void claimAll()}>Claim all {claimable.length}</Button>}
        </div>
      </ScreenHero>

      {aurora && !aurora.all_done && aurora.objectives && (
        <QuestSection id="aurora" className="qs-aurora" title={`Aurora · chapter ${aurora.level ?? ""}`} badge={aurora.done ? 1 : undefined} aside={aurora.done ? "complete" : `${aurora.objectives.filter((o) => Number(o.done) === 1).length} of ${aurora.objectives.length}`}>
          {aurora.descr && <p className="gd-tagline">{aurora.descr}</p>}
          <div className="rows">
            {aurora.objectives.map((o, i) => {
              const num = Number(o.award?.numerator ?? 0), den = Number(o.award?.denominator ?? 0);
              return (
                <Row key={`${o.ref_key}-${i}`} className={cx("qs-row", Number(o.done) === 1 && "done")}>
                  <QuestMedal refKey={o.ref_key} />
                  <RowText line={<b className="creamt" style={{ fontWeight: 600 }}>{o.name}</b>} meta={<>{o.obj}<span className="qs-obj"><Bar value={den ? Math.min(1, num / den) : Number(o.done) === 1 ? 1 : 0} color={Number(o.done) === 1 ? "green" : "gold"} /><small>{den ? `${fmtNum(Math.min(num, den))} / ${fmtNum(den)}` : Number(o.done) === 1 ? "done" : ""}</small></span></>} />
                </Row>
              );
            })}
          </div>
          <div className="lb-foot">
            <span>Reward: <b className="gold">{awardWords(aurora.award, itemNames) || "—"}</b></span>
            {aurora.done ? <Button size="sm" kind="gold" busy={actions.isBusy("claim-aurora")} onClick={() => void actions.run("claim-aurora", () => api.quest.collect({ wid, vid: vid! }, "aurora"), { success: "Aurora chapter claimed." })}>Claim</Button> : null}
          </div>
        </QuestSection>
      )}
      {aurora?.all_done && <p className="c-note">Every Aurora chapter is claimed.</p>}

      {grouped.map((c) => (
        <QuestSection key={c.name} id={`cat.${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} title={c.name} badge={c.items.filter((q) => Number(q.done) === 1).length || undefined} aside={plural(c.items.length, "quest")}>
          <div className="rows">
            {c.items.map((q) => {
              const done = Number(q.done) === 1;
              const to = questTarget(q, base, vid);
              return (
                <Row key={q.id} className={cx("qs-row", done && "done")}>
                  <QuestMedal refKey={q.ref_key || q.type} />
                  <RowText line={<b className="creamt" style={{ fontWeight: 600 }}>{q.name}{q.level > 1 ? <span className="muted"> · {q.level}</span> : null}</b>} meta={<>{q.obj}<span className="qs-award">Reward: <b>{awardWords(q.award, itemNames) || "—"}</b></span></>} />
                  <div className="gd-acts">
                    {done ? (
                      <Button size="sm" kind="gold" busy={actions.isBusy(`claim-${q.id}`)} onClick={() => void claim(q)}>Claim</Button>
                    ) : to ? (
                      <Button size="sm" kind="ghost" onClick={() => nav(to)}>Go <IconChevronRight /></Button>
                    ) : null}
                  </div>
                </Row>
              );
            })}
          </div>
        </QuestSection>
      ))}
      {!quests.length && <Card><Empty>No quests are open in this city right now.</Empty></Card>}
      {unlock && <p className="c-note">{unlock}</p>}
    </div>
  );
}

/** A quest band: closed until the player opens it, then remembered (Ben's rule for every collapsible list). */
function QuestSection({ id, title, badge, aside, className, children }: { id: string; title: ReactNode; badge?: number; aside?: ReactNode; className?: string; children: ReactNode }) {
  const [open, toggle] = useRememberedOpen(`quests.${id}`);
  return (
    <Card small={!open} className={cx("qs-section", open && "open", className)} data-section={id}>
      <CardHead title={title} badge={badge} aside={aside} onToggle={toggle} open={open} />
      {open && children}
    </Card>
  );
}

function QuestMedal({ refKey }: { refKey: string }) {
  if ((BUILDING_KEYS as readonly string[]).includes(refKey)) return <Medallion src={art.med(refKey)} alt="" size={56} />;
  if ((TROOP_KEYS as readonly string[]).includes(refKey)) return <UnitMedal troop={refKey as TroopKey} tier={0} size={56} />;
  if (refKey === "amulet" || refKey === "wheel") return <Medallion src={art.store.amulet} alt="" size={56} />;
  return <Medallion size={56}><IconStar /></Medallion>;
}

/** Where a quest points: a building's page, a recruit screen, the wheel. */
function questTarget(q: QuestItem, base: string, vid: number | null): string | null {
  if (!vid) return null;
  const k = q.ref_key || q.type;
  if ((BUILDING_KEYS as readonly string[]).includes(k)) {
    const b = k as BuildingKey;
    if (b === "b_hq") return buildLink(`${base}/city/${vid}`, "b_hq");
    if (b === "b_b" || b === "b_st" || b === "b_wo") return `${base}/city/${vid}/recruit/${b}`;
    if (b === "b_a") return `${base}/city/${vid}/academy`;
    if (b === "b_r") return `${base}/city/${vid}/rally`;
    if (b === "b_m") return `${base}/city/${vid}/market`;
    if (b === "b_s") return `${base}/city/${vid}/forge`;
    return `${base}/city/${vid}/b/${b}`;
  }
  if ((TROOP_KEYS as readonly string[]).includes(k)) {
    const t = k as TroopKey;
    const building = t === "t_sf" || t === "t_sw" || t === "t_a" ? "b_b" : t === "t_sc" || t === "t_lc" || t === "t_hc" ? "b_st" : t === "t_n" ? null : "b_wo";
    return building ? `${base}/city/${vid}/recruit/${building}` : `${base}/city/${vid}/academy`;
  }
  if (k === "amulet" || k === "wheel") return `${base}/city/${vid}/wheel`;
  if (k === "points" || k === "resource_production") return buildLink(`${base}/city/${vid}`);
  if (k === "mail") return `${base}/mail/new`;
  if (k.startsWith("rd_")) return `${base}/city/${vid}/forge`;
  return null;
}
